/* eslint-disable tailwindcss/no-arbitrary-value */
import classNames from 'classnames';
import { useState } from 'react';
import { MaterialIcon } from '../../../../components/MaterialIcon';
import { ProductDetailsProps } from '../../ProductDetails';
import { PushNavInteraction } from './navTracking';
import {
  DEFAULT_STICKY_OFFSET,
  INavigationItem,
  STICKY_NAV_ACTIVE_TEXT,
} from './StickyNavigationBar';
import { StickyNavigationBarApplyButton } from './StickyNavigationBarApplyButton';
import { scrollToSection, useActiveSection } from './useActiveSection';

const DROPDOWN_ID = 'sticky-nav-mobile-dropdown';

type StickyNavigationBarMobileProps = ProductDetailsProps & {
  navigationItems: INavigationItem[];
  stickyOffset?: number;
  applyLabel?: string;
  itemListName?: string;
  /** The nav's own interaction event (default vs TI payload). */
  pushNavInteraction: PushNavInteraction;
  /** Whether the bar carries the Apply CTA — see `StickyNavigationBarDesktop`. */
  showApplyCta?: boolean;
};

export const StickyNavigationBarMobile: React.FC<
  StickyNavigationBarMobileProps
> = (props) => {
  const {
    navigationItems,
    applyLabel,
    product,
    pushDataLayer,
    itemListName,
    pushNavInteraction,
    showApplyCta = false,
  } = props;
  const stickyOffset = props.stickyOffset ?? DEFAULT_STICKY_OFFSET;
  const [selectedId, setSelectedManually] = useActiveSection(
    navigationItems.map((i) => i.id),
    stickyOffset,
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const getNavigationItemLabel = (id: string) => {
    return navigationItems.find((item) => item.id === id)?.label ?? '';
  };

  const handleSelect = (id: string) => {
    setSelectedManually(id);
    setIsDropdownOpen(false);

    requestAnimationFrame(() => {
      scrollToSection(id, stickyOffset);
    });

    // Tab-navigation tracking. The event payload is the nav's own
    // `pushNavInteraction`, shared with the desktop bar so both stay in parity.
    const label = getNavigationItemLabel(id);
    if (label && pushDataLayer) {
      const index = navigationItems.findIndex((item) => item.id === id);
      pushNavInteraction({
        viewport: 'mobile',
        label,
        index,
        product,
        itemListName,
        pushDataLayer,
      });
    }
  };

  return (
    <div className="relative">
      <div className="flex justify-between gap-2 items-center">
        <div
          className={`py-spacing/padding/y pr-spacing/padding/x ${STICKY_NAV_ACTIVE_TEXT}`}
        >
          <button
            type="button"
            className="flex w-full cursor-pointer items-center justify-between gap-spacing/gap/soft-8px"
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            aria-expanded={isDropdownOpen}
            aria-controls={DROPDOWN_ID}
          >
            <div className="mhg-alpha-tag-font">
              {getNavigationItemLabel(selectedId)}
            </div>
            <MaterialIcon
              icon="expand_less"
              className={`transition-transform ${isDropdownOpen ? 'rotate-0' : 'rotate-180'}`}
            />
          </button>
        </div>
        {/* Content-sized: the TI label ("Instant Checkout") is wider than the
            former fixed 80px slot and spilled past the button (CMS-1991 QA). */}
        {showApplyCta && (
          <div className="shrink-0">
            <StickyNavigationBarApplyButton {...props} label={applyLabel} />
          </div>
        )}
      </div>
      <NavigationDropdown
        id={DROPDOWN_ID}
        items={navigationItems}
        selectedId={selectedId}
        isOpen={isDropdownOpen}
        onSelect={handleSelect}
      />
    </div>
  );
};

type NavigationDropdownProps = {
  id: string;
  items: INavigationItem[];
  selectedId: string;
  isOpen: boolean;
  onSelect: (id: string) => void;
};

const NavigationDropdown: React.FC<NavigationDropdownProps> = ({
  id,
  items,
  selectedId,
  isOpen,
  onSelect,
}) => {
  return (
    <div
      id={id}
      role="listbox"
      aria-hidden={!isOpen}
      className={classNames(
        'absolute inset-x-0 top-full z-10 overflow-hidden border border-borders/default rounded-sm bg-fill/alternative-1 transition-all duration-200 ease-in-out',
        {
          'visible max-h-fit opacity-100': isOpen,
          'invisible max-h-0 opacity-0': !isOpen,
        },
      )}
    >
      <ul className="flex flex-col divide-y divide-borders/default">
        {items.map((item) => {
          const isSelected = item.id === selectedId;
          return (
            <li key={item.id}>
              <button
                type="button"
                role="option"
                aria-selected={isSelected}
                tabIndex={isOpen ? 0 : -1}
                onClick={() => onSelect(item.id)}
                className={classNames(
                  'mhg-alpha-tag-font w-full text-left py-spacing/padding/y px-spacing/padding/x',
                  isSelected ? STICKY_NAV_ACTIVE_TEXT : 'text-fill/primary',
                )}
              >
                {item.label}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
